import { Controller } from "@hotwired/stimulus";
import { isEmpty } from "lodash";
import { useStore } from "stimulus-store";

import { deploymentStatusStore } from "../stores/deployment_status_store";
import { regionStore } from "../stores/locales/region_store";
import { buildingStore } from "../stores/locales/building_store";
import { areaStore } from "../stores/locales/area_store";
import { roomStore } from "../stores/locales/room_store";
import { allDevicesStore } from "../stores/devices/all_devices_store";
import { allBundlesStore } from "../stores/device_types/all_bundles_store";
import { bundleStore } from "../stores/device_types/bundle_store";
import { isDevice } from "shared/device_types/device_type_id_helpers";

import { getCsrfToken } from "shared/form_helpers";
import { sendFailureNotification, sendSuccessNotification } from "shared/toast_notifications";
import { getBundle } from "shared/device_types/bundles";
import { getDevicesInBundleInstance } from "shared/devices/device_discovery";

/**
 * Update Bundle Form Controller
 *
 * STORE INTEGRATION:
 * - Uses stimulus-store convention with on<StoreName>Update methods
 * - Reactive updates: onRegionStoreUpdate, onBuildingStoreUpdate, etc.
 * - Store values accessed via this.<storeName>Value pattern
 *
 * TURBO DRIVE NOTE: All page-specific stores (regionStore, buildingStore,
 * areaStore, roomStore, bundleStore, deploymentStatusStore) are module-level
 * singletons that persist across Turbo navigations. This page pre-populates
 * locale stores from the bundle instance's saved location data. The locale
 * stores (region/building/area/room) are cleared by their own select
 * controllers' disconnect() methods when leaving the previous page (see
 * region_select_controller.js), so stale values shouldn't be visible here;
 * bundleStore and deploymentStatusStore may still need explicit clearing in
 * connect() if issues arise.
 */

// Connects to data-controller="update-bundle-form"
export default class extends Controller {

    static stores = [
        deploymentStatusStore,
        regionStore,
        buildingStore,
        areaStore,
        roomStore,
        allDevicesStore,
        allBundlesStore,
        bundleStore
    ]

    static targets = [
        "bundleNameInput",
        "regionSelect",
        "buildingSelect",
        "areaSelect",
        "roomSelect",
        "deviceConfiguration",
        "deviceConfigurationForms",
        "floorPlanSection",
        "testingTasksContainer",
        "currentRegionId",
        "currentBuildingId",
        "currentAreaId",
        "currentRoomId",
        "currentXCoordinate",
        "currentYCoordinate",
        "bundleId",
        "bundleInstanceId"
    ]

    connect() {
        useStore(this);
        this.initializeForm();
        this.setupResponsiveBehavior();
    }

    initialize() {
        this.deviceConfigurations = {};
        this.isMobile = false;
        this.isTablet = false;

        // Populate the allDevicesStore with the devices in this bundle and the
        // allBundlesStore with the bundle type this page is updating an instance
        // of. This is needed in the floor plan controllers to render the icons.
        this.populateStores();
    }

    disconnect() {
        // Clean up periodic state synchronization
        if (this.stateSyncInterval) {
            clearInterval(this.stateSyncInterval);
            this.stateSyncInterval = null;
        }

        // Clean up responsive behavior
        this.cleanupResponsiveBehavior();
    }

    populateStores() {
        // Marshall the inputs
        const bundleId = this.bundleIdTarget.value;
        const bundleInstanceId = this.bundleInstanceIdTarget.value;

        // Issue the queries to populate the stores
        getBundle(bundleId).then(bundle => {
            this.setAllBundlesStoreValue([bundle])
            this.setBundleStoreValue(bundle)  // Set the current bundle for floor plan interactions
        })

        getDevicesInBundleInstance(bundleId, bundleInstanceId).then(devices => {
            this.setAllDevicesStoreValue(devices)
        })
    }

    setupResponsiveBehavior() {
        // Detect device type
        this.updateDeviceType();

        // Set up resize listener
        this.resizeHandler = () => {
            this.updateDeviceType();
            this.handleResponsiveChanges();
        };

        window.addEventListener('resize', this.resizeHandler);

        // Initial responsive setup
        this.handleResponsiveChanges();
    }

    cleanupResponsiveBehavior() {
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
            this.resizeHandler = null;
        }
    }

    updateDeviceType() {
        const width = window.innerWidth;
        this.isMobile = width <= 767;
        this.isTablet = width >= 768 && width <= 991;
        this.isDesktop = width >= 992;
    }

    handleResponsiveChanges() {
        this.updateButtonBehavior();
        this.updateFormLayout();
        this.updateFloorPlanBehavior();
    }

    updateButtonBehavior() {
        // Handle mobile vs desktop update buttons
        const desktopButton = document.getElementById('update-bundle');
        const mobileButton = document.getElementById('update-bundle-mobile');

        if (this.isMobile) {
            // Enable mobile button, disable desktop button
            if (mobileButton) {
                mobileButton.style.display = 'inline-block';
                this.syncButtonState(mobileButton);
            }
            if (desktopButton) {
                desktopButton.style.display = 'none';
            }
        } else {
            // Enable desktop button, hide mobile button
            if (desktopButton) {
                desktopButton.style.display = 'inline-block';
                this.syncButtonState(desktopButton);
            }
            if (mobileButton) {
                mobileButton.style.display = 'none';
            }
        }
    }

    syncButtonState(button) {
        if (this.isFormValid()) {
            button.disabled = false;
            button.classList.remove('btn-secondary');
            button.classList.add('btn-primary');
        } else {
            button.disabled = true;
            button.classList.remove('btn-primary');
            button.classList.add('btn-secondary');
        }
    }

    updateFormLayout() {
        // Adjust form layout based on screen size
        const deviceConfigCards = this.element.querySelectorAll('.device-config-card');

        deviceConfigCards.forEach(card => {
            if (this.isMobile) {
                // Stack form fields vertically on mobile
                card.classList.add('mobile-layout');
            } else {
                card.classList.remove('mobile-layout');
            }
        });
    }

    updateFloorPlanBehavior() {
        // Floor plan behavior is now handled by the walkthrough controllers
        // No custom logic needed here
    }

    loadExistingCoordinates() {
        // Load existing coordinates into the walkthrough controllers if they exist
        const currentX = this.hasCurrentXCoordinateTarget ? this.currentXCoordinateTarget.value : '';
        const currentY = this.hasCurrentYCoordinateTarget ? this.currentYCoordinateTarget.value : '';

        if (currentX && currentY) {
            // Find the coordinate fields managed by the walkthrough controllers
            const xField = document.querySelector('[data-floorplans--floor-plan-icon-manager-target="coordinateX"]');
            const yField = document.querySelector('[data-floorplans--floor-plan-icon-manager-target="coordinateY"]');

            if (xField && yField) {
                xField.value = currentX;
                yField.value = currentY;
            }
        }
    }

    initializeForm() {
        // Pre-populate form with current bundle instance data
        this.prePopulateCurrentLocation();
        this.prePopulateDeviceConfigurations();

        // Initialize device configurations object
        this.initializeDeviceConfigurations();

        // Set up event listeners
        this.setupDeviceConfigurationEventListeners();

        // Disable update button initially
        this.disableUpdateButton();

        // Update button state based on form validity
        this.updateButtonState();
    }

    // Stimulus-store reactive update methods
    onDeploymentStatusStoreUpdate(newValue) {
        this.updateButtonState();
    }

    onRegionStoreUpdate(newValue) {
        this.updateButtonState();
    }

    onBuildingStoreUpdate(newValue) {
        this.updateButtonState();
    }

    onAreaStoreUpdate(newValue) {
        // The floor-plan controller will handle the display automatically
        // We just need to show/hide the floor plan section and update button state
        if (!isEmpty(newValue)) {
            this.showFloorPlanSection();
            // Load existing coordinates into the walkthrough controllers if they exist
            this.loadExistingCoordinates();
        } else {
            this.hideFloorPlanSection();
        }
        this.updateButtonState();
    }

    onRoomStoreUpdate(newValue) {
        this.updateButtonState();
    }

    // Pre-population methods
    prePopulateCurrentLocation() {
        // Get current location values from target elements
        const currentRegionId = this.hasCurrentRegionIdTarget ? this.currentRegionIdTarget.value : '';
        const currentBuildingId = this.hasCurrentBuildingIdTarget ? this.currentBuildingIdTarget.value : '';
        const currentAreaId = this.hasCurrentAreaIdTarget ? this.currentAreaIdTarget.value : '';
        const currentRoomId = this.hasCurrentRoomIdTarget ? this.currentRoomIdTarget.value : '';

        // Pre-select current values in location selects
        if (currentRegionId && this.hasRegionSelectTarget) {
            // The region select controller will handle loading and selecting the current value
            this.regionSelectTarget.setAttribute('data-current-value', currentRegionId);
        }

        if (currentBuildingId && this.hasBuildingSelectTarget) {
            this.buildingSelectTarget.setAttribute('data-current-value', currentBuildingId);
        }

        if (currentAreaId && this.hasAreaSelectTarget) {
            this.areaSelectTarget.setAttribute('data-current-value', currentAreaId);
        }

        if (currentRoomId && this.hasRoomSelectTarget) {
            this.roomSelectTarget.setAttribute('data-current-value', currentRoomId);
        }
    }

    prePopulateDeviceConfigurations() {
        // Device configurations are already pre-populated in the ERB template
        // This method handles any additional JavaScript-based initialization (only devices, not parts)
        const deviceConfigCards = this.element.querySelectorAll('.device-config-card');

        deviceConfigCards.forEach(card => {
            const deviceType = card.dataset.deviceType;

            // Only pre-populate configurations for actual devices
            if (deviceType && isDevice({ device_type_definition: deviceType })) {
                const deviceConfigInputs = card.querySelectorAll('.device-config-input');

                deviceConfigInputs.forEach(input => {
                    if (input.dataset.property === 'deployment_status' && input.value) {
                        // Pre-select deployment status values
                        input.setAttribute('data-current-value', input.value);
                    }
                });
            }
        });
    }

    initializeDeviceConfigurations() {
        // Initialize device configurations object from form values (only devices, not parts)
        const deviceConfigCards = this.element.querySelectorAll('.device-config-card');

        deviceConfigCards.forEach(card => {
            const deviceType = card.dataset.deviceType;

            // Only initialize configurations for actual devices
            if (deviceType && isDevice({ device_type_definition: deviceType })) {
                const deviceConfigInputs = card.querySelectorAll('.device-config-input');

                deviceConfigInputs.forEach(input => {
                    const deviceId = input.dataset.deviceId;
                    const property = input.dataset.property;

                    if (deviceId && property) {
                        if (!this.deviceConfigurations[deviceId]) {
                            this.deviceConfigurations[deviceId] = {};
                        }
                        this.deviceConfigurations[deviceId][property] = input.value || '';
                    }
                });
            }
        });
    }

    setupDeviceConfigurationEventListeners() {

        // Only set up listeners for device configuration inputs (not parts)
        const deviceConfigCards = this.element.querySelectorAll('.device-config-card');

        deviceConfigCards.forEach(card => {
            const deviceType = card.dataset.deviceType;

            // Only set up listeners for actual devices
            if (deviceType && isDevice({ device_type_definition: deviceType })) {
                const configInputs = card.querySelectorAll('.device-config-input');

                configInputs.forEach(input => {
                    if (!input || !input.dataset) {
                        console.warn('Invalid input element found in device configuration:', input);
                        return;
                    }

                    // Add event listeners for all form field types
                    input.addEventListener('input', (e) => {
                        this.handleDeviceConfigurationChange(e.target);
                    });

                    input.addEventListener('change', (e) => {
                        this.handleDeviceConfigurationChange(e.target);
                    });

                    input.addEventListener('blur', (e) => {
                        this.handleDeviceConfigurationChange(e.target);
                    });
                });
            }
        });

        // Add event listener for bundle name input
        if (this.hasBundleNameInputTarget) {
            this.bundleNameInputTarget.addEventListener('input', () => {
                this.updateButtonState();
            });

            this.bundleNameInputTarget.addEventListener('change', () => {
                this.updateButtonState();
            });

            this.bundleNameInputTarget.addEventListener('blur', () => {
                this.updateButtonState();
            });
        }

        // Set up periodic state synchronization
        this.setupPeriodicStateSynchronization();
    }

    handleDeviceConfigurationChange(input) {
        this.updateDeviceConfiguration(input);
        this.syncDeviceConfigurationState();
        this.updateButtonState();
    }

    updateDeviceConfiguration(input) {
        const deviceId = input.dataset.deviceId;
        const property = input.dataset.property;

        if (!deviceId || !property) {
            return;
        }

        if (!this.deviceConfigurations[deviceId]) {
            this.deviceConfigurations[deviceId] = {};
        }

        this.deviceConfigurations[deviceId][property] = input.value || '';
    }

    setupPeriodicStateSynchronization() {
        if (this.stateSyncInterval) {
            clearInterval(this.stateSyncInterval);
        }

        // Sync state every 500ms when form is active
        this.stateSyncInterval = setInterval(() => {
            if (this.isFormActive()) {
                this.syncDeviceConfigurationState();
            }
        }, 500);
    }

    isFormActive() {
        const activeElement = document.activeElement;
        if (!activeElement) {
            return false;
        }

        return this.element.contains(activeElement) && activeElement.classList.contains('device-config-input');
    }

    syncDeviceConfigurationState() {
        try {
            // Only sync device configuration inputs (not parts)
            const deviceConfigCards = this.element.querySelectorAll('.device-config-card');

            deviceConfigCards.forEach(card => {
                const deviceType = card.dataset.deviceType;

                // Only sync configurations for actual devices
                if (deviceType && isDevice({ device_type_definition: deviceType })) {
                    const configInputs = card.querySelectorAll('.device-config-input');

                    configInputs.forEach(input => {
                        if (!input || !input.dataset) {
                            return;
                        }

                        const deviceId = input.dataset.deviceId;
                        const property = input.dataset.property;

                        if (!deviceId || !property) {
                            return;
                        }

                        if (!this.deviceConfigurations[deviceId]) {
                            this.deviceConfigurations[deviceId] = {};
                        }

                        let currentValue = '';
                        try {
                            if (input.tomselect && typeof input.tomselect.getValue === 'function') {
                                currentValue = input.tomselect.getValue() || '';
                            } else {
                                currentValue = (input.value || '').trim();
                            }
                        } catch (error) {
                            console.warn('Error getting value from input element:', error);
                            currentValue = input.value || '';
                        }

                        const storedValue = this.deviceConfigurations[deviceId][property];
                        if (storedValue !== currentValue) {
                            this.deviceConfigurations[deviceId][property] = currentValue;
                        }
                    });
                }
            });
        } catch (error) {
            console.error('Error in syncDeviceConfigurationState:', error);
        }
    }

    // Form validation and button state
    updateButtonState() {
        if (this.isFormValid()) {
            this.enableUpdateButton();
        } else {
            this.disableUpdateButton();
        }
    }

    isFormValid() {
        // Check if at least one field has been modified
        const hasChanges = this.hasLocationChanges() || this.hasDeviceConfigurationChanges() || this.hasTestingTaskChanges();
        return hasChanges;
    }

    hasLocationChanges() {
        // Check if location has been changed from current values
        const currentRegionId = this.hasCurrentRegionIdTarget ? this.currentRegionIdTarget.value : '';
        const currentBuildingId = this.hasCurrentBuildingIdTarget ? this.currentBuildingIdTarget.value : '';
        const currentAreaId = this.hasCurrentAreaIdTarget ? this.currentAreaIdTarget.value : '';
        const currentRoomId = this.hasCurrentRoomIdTarget ? this.currentRoomIdTarget.value : '';

        const selectedRegion = this.regionStoreValue?.id || '';
        const selectedBuilding = this.buildingStoreValue?.id || '';
        const selectedArea = this.areaStoreValue?.id || '';
        const selectedRoom = this.roomStoreValue?.id || '';

        return (selectedRegion !== currentRegionId) || (selectedBuilding !== currentBuildingId) || (selectedArea !== currentAreaId) || (selectedRoom !== currentRoomId);
    }

    hasDeviceConfigurationChanges() {
        // Check if any device configuration has been modified (only devices, not parts)
        const deviceConfigCards = this.element.querySelectorAll('.device-config-card');

        for (const card of deviceConfigCards) {
            const deviceType = card.dataset.deviceType;

            // Only check changes for actual devices
            if (deviceType && isDevice({ device_type_definition: deviceType })) {
                const configInputs = card.querySelectorAll('.device-config-input');

                for (const input of configInputs) {
                    const originalValue = input.defaultValue || '';
                    const currentValue = input.value || '';

                    if (originalValue !== currentValue) {
                        return true;
                    }
                }
            }
        }

        return false;
    }

    hasTestingTaskChanges() {
        // Check if any testing task status has been changed by looking for testing task form data
        const testingTaskInputs = this.element.querySelectorAll('input[name^="testing_task"]');
        return testingTaskInputs.length > 0 && Array.from(testingTaskInputs).some(input => input.checked);
    }

    enableUpdateButton() {
        // Enable both desktop and mobile buttons
        const desktopButton = document.getElementById('update-bundle');
        const mobileButton = document.getElementById('update-bundle-mobile');

        if (desktopButton) {
            desktopButton.disabled = false;
            desktopButton.classList.remove('btn-secondary');
            desktopButton.classList.add('btn-primary');
        }

        if (mobileButton) {
            mobileButton.disabled = false;
            mobileButton.classList.remove('btn-secondary');
            mobileButton.classList.add('btn-primary');
        }
    }

    disableUpdateButton() {
        // Disable both desktop and mobile buttons
        const desktopButton = document.getElementById('update-bundle');
        const mobileButton = document.getElementById('update-bundle-mobile');

        if (desktopButton) {
            desktopButton.disabled = true;
            desktopButton.classList.remove('btn-primary');
            desktopButton.classList.add('btn-secondary');
        }

        if (mobileButton) {
            mobileButton.disabled = true;
            mobileButton.classList.remove('btn-primary');
            mobileButton.classList.add('btn-secondary');
        }
    }

    // Action method for delete button
    async deleteBundle(event) {
        if (event) {
            event.preventDefault();
        }

        const bundleInstanceId = this.bundleInstanceIdTarget.value;
        const bundleId = this.bundleIdTarget.value;

        if (!confirm('Are you sure you want to delete this bundle instance?')) {
            return;
        }

        try {
            const response = await fetch('/api/items', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken(),
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    bundle: bundleId,
                    bundle_instance_id: bundleInstanceId
                })
            });

            if (response.ok) {
                sendSuccessNotification('Bundle instance deleted successfully');
                window.location.href = '/bundles/manage';
            } else {
                const errorData = await response.json().catch(() => ({}));
                sendFailureNotification(errorData.message || 'Failed to delete bundle instance');
            }
        } catch (error) {
            sendFailureNotification(error.message || 'Failed to delete bundle instance');
        }
    }

    // Action method for update button
    async updateBundle(event) {
        if (event) {
            event.preventDefault();
        }

        await this.performUpdate();
    }

    async performUpdate() {
        if (!this.isFormValid()) {
            return;
        }

        try {
            this.disableFormDuringUpdate();
            this.showUpdateProgress();

            const updateParams = this.formatUpdatePayload();
            const response = await this.submitUpdate(updateParams);

            if (response.status === "success") {
                sendSuccessNotification('Bundle instance updated successfully.');
                this.redirectToManagePage();
            } else {
                this.handleUpdateError(response);
            }
        } catch (error) {
            sendFailureNotification(`Unable to update bundle! ${ error.message }`);
        } finally {
            this.enableFormAfterUpdate();
            this.hideUpdateProgress();
        }
    }

    formatUpdatePayload() {
        const bundleInstanceId = document.getElementById('bundle-instance-id')?.value;
        const bundleId = document.getElementById('bundle-id')?.value;

        // Get the bundle name from the single input field
        const bundleDeviceName = this.hasBundleNameInputTarget ? this.bundleNameInputTarget.value?.trim() || '' : '';

        // Build the base payload with bundle-wide updates
        const payload = {
            bundle: bundleId, bundle_instance: bundleInstanceId, name: bundleDeviceName
        };

        // Add bundle-wide location update if location has changed
        const region = this.regionStoreValue;
        const building = this.buildingStoreValue;
        const area = this.areaStoreValue;
        const room = this.roomStoreValue;

        if (region && building && area && room) {
            payload.locale = {
                region: region.id, building: building.id, area: area.id, room: room.id
            };

            // Get floor plan coordinates from the walkthrough controllers
            const xField = document.querySelector('[data-floorplans--floor-plan-icon-manager-target="coordinateX"]');
            const yField = document.querySelector('[data-floorplans--floor-plan-icon-manager-target="coordinateY"]');

            if (xField && yField && xField.value && yField.value) {
                payload.locale.floorplan_x_coordinate = parseFloat(xField.value);
                payload.locale.floorplan_y_coordinate = parseFloat(yField.value);
            }
        }

        // Collect device-specific updates - only include devices that have individual changes
        const items = [];
        const deviceConfigCards = this.element.querySelectorAll('.device-config-card');

        deviceConfigCards.forEach((card) => {
            const deviceId = card.dataset.deviceId;

            if (deviceId) {
                const deviceUpdate = {
                    id: deviceId
                };

                let hasDeviceSpecificChanges = false;

                // Check all device-specific inputs in this card
                const deviceInputs = card.querySelectorAll('.device-config-input');

                deviceInputs.forEach(input => {
                    const property = input.dataset.property;
                    const currentValue = input.value?.trim() || '';
                    const originalValue = input.defaultValue?.trim() || '';

                    // Only include properties that have changed from their original values
                    if (property && currentValue !== originalValue) {
                        switch (property) {
                            case 'deployment_status':
                                if (currentValue) {
                                    deviceUpdate.deployment_status = currentValue;
                                    hasDeviceSpecificChanges = true;
                                }
                                break;
                            case 'ip_address':
                                deviceUpdate.ip_address = currentValue || null;
                                hasDeviceSpecificChanges = true;
                                break;
                            case 'mac_address':
                                deviceUpdate.mac_address = currentValue || null;
                                hasDeviceSpecificChanges = true;
                                break;
                            case 'dns_name':
                                deviceUpdate.dns_name = currentValue || null;
                                hasDeviceSpecificChanges = true;
                                break;
                            case 'port_id':
                                deviceUpdate.port_id = currentValue || null;
                                hasDeviceSpecificChanges = true;
                                break;
                            // Add other device-specific properties as needed
                        }
                    }
                });

                // Add testing task updates for this device
                const testingTaskUpdates = this.getTestingTaskUpdatesForDevice(deviceId);
                if (testingTaskUpdates.length > 0) {
                    deviceUpdate.testing_tasks = testingTaskUpdates;
                    hasDeviceSpecificChanges = true;
                }

                // Only include this device if it has specific changes
                if (hasDeviceSpecificChanges) {
                    items.push(deviceUpdate);
                }
            }
        });

        // Add items array only if there are device-specific updates
        if (items.length > 0) {
            payload.items = items;  // ← Fixed: use 'items' not 'devices'
        }

        return payload;
    }

    getTestingTaskUpdatesForDevice(deviceId) {
        const testingTaskUpdates = [];

        // Find all testing task radio inputs that are checked
        // The testing-task-cards controller generates inputs with names like: testing_task[task_id]
        const testingTaskInputs = this.element.querySelectorAll(`input[name^="testing_task"]:checked`);

        testingTaskInputs.forEach(input => {
            // Parse the input name to extract task ID
            // Format: testing_task[task_id] = status_id
            const nameMatch = input.name.match(/testing_task\[(.+)\]/);
            if (nameMatch) {
                const taskId = nameMatch[1];
                const statusId = input.value;

                if (taskId && statusId) {
                    testingTaskUpdates.push({
                        id: taskId, state: statusId
                    });
                }
            }
        });

        return testingTaskUpdates;
    }

    async submitUpdate(params) {
        const response = await fetch('/api/items', {
            method: 'PUT', headers: {
                'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken(), 'Accept': 'application/json'
            }, body: JSON.stringify(params)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `HTTP ${ response.status }: ${ response.statusText }`);
        }

        return await response.json();
    }

    disableFormDuringUpdate() {
        this.disableUpdateButton();

        // Disable all form inputs
        const inputs = this.element.querySelectorAll('input, select');
        inputs.forEach(input => {
            input.disabled = true;
        });
    }

    enableFormAfterUpdate() {
        // Re-enable all form inputs
        const inputs = this.element.querySelectorAll('input, select');
        inputs.forEach(input => {
            input.disabled = false;
        });

        // Re-disable bundle name input if there are no devices
        const deviceConfigCards = this.element.querySelectorAll('.device-config-card');
        const hasDevices = Array.from(deviceConfigCards).some(card => {
            const deviceType = card.dataset.deviceType;
            return deviceType && isDevice({ device_type_definition: deviceType });
        });

        if (!hasDevices && this.hasBundleNameInputTarget) {
            this.bundleNameInputTarget.disabled = true;
        }

        // Update button will be re-enabled based on form validation
        this.updateButtonState();
    }

    showUpdateProgress() {
        // Show progress on both desktop and mobile buttons
        const desktopButton = document.getElementById('update-bundle');
        const mobileButton = document.getElementById('update-bundle-mobile');

        [desktopButton, mobileButton].forEach(button => {
            if (button) {
                const originalText = button.textContent;
                button.innerHTML = '<i class="fas fa-spinner fa-spin me-2"></i>Updating...';
                button.setAttribute('data-original-text', originalText);
            }
        });
    }

    hideUpdateProgress() {
        // Hide progress on both desktop and mobile buttons
        const desktopButton = document.getElementById('update-bundle');
        const mobileButton = document.getElementById('update-bundle-mobile');

        [desktopButton, mobileButton].forEach(button => {
            if (button) {
                const originalText = button.getAttribute('data-original-text');
                if (originalText) {
                    button.textContent = originalText;
                    button.removeAttribute('data-original-text');
                }
            }
        });
    }

    handleUpdateError(response) {
        const error = new Error(response.message || 'Update failed');
        sendFailureNotification(`Update failed! ${ error.message }`);
    }

    redirectToManagePage() {
        // Redirect to manage bundles page after a successful update
        setTimeout(() => {
            window.location.href = '/bundles/manage';
        }, 1500);
    }

    showFloorPlanSection() {
        if (this.hasFloorPlanSectionTarget) {
            this.floorPlanSectionTarget.style.display = 'block';
        }
    }

    hideFloorPlanSection() {
        if (this.hasFloorPlanSectionTarget) {
            this.floorPlanSectionTarget.style.display = 'none';
        }
    }
}